// Wall-graph model for the house floor-plan editor (Stage 9a).
//
// A floor plan is a planar graph in the house's LOCAL metres frame
// (x east, y south — same convention as the plot editor):
//   { vertices: [{ id, xM, yM }],
//     walls:    [{ id, a, b, thicknessM, kind }],
//     _seq }                       // monotonic id counter, persisted
//
// `a`/`b` reference vertex ids. Rooms are NOT modelled here yet — they are
// detected as enclosed faces in Stage 9b. For now the graph is just the set
// of welded wall segments produced by the room-rectangle tool.
//
// Pure JS, no DOM, no Konva — unit-tested with Vitest. All mutators return a
// NEW graph (the input is never modified) so the editor's snapshot/undo stack
// can hold previous graphs without aliasing.

const DEFAULT_THICKNESS_M = 0.2
// Two corners closer than this are treated as the same vertex. 5 cm is well
// below any real wall thickness, so rooms that visually share an edge weld,
// while distinct-but-near walls stay separate.
const WELD_TOLERANCE_M = 0.05

export function createGraph() {
  return { vertices: [], walls: [], _seq: 0 }
}

// Defensive deep clone — callers (and snapshots) must never share vertex/wall
// objects with a graph that may later be edited in place by Konva handlers.
export function cloneGraph(graph) {
  if (!graph) return createGraph()
  return {
    vertices: (graph.vertices ?? []).map((v) => ({ ...v })),
    walls: (graph.walls ?? []).map((w) => ({ ...w })),
    _seq: graph._seq ?? 0,
  }
}

export function vertexAt(graph, xM, yM, tol = WELD_TOLERANCE_M) {
  for (const v of graph.vertices) {
    if (Math.hypot(v.xM - xM, v.yM - yM) <= tol) return v
  }
  return null
}

export function boundingBox(graph) {
  if (!graph.vertices.length) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0, widthM: 0, heightM: 0 }
  }
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const v of graph.vertices) {
    if (v.xM < minX) minX = v.xM
    if (v.yM < minY) minY = v.yM
    if (v.xM > maxX) maxX = v.xM
    if (v.yM > maxY) maxY = v.yM
  }
  return { minX, minY, maxX, maxY, widthM: maxX - minX, heightM: maxY - minY }
}

export function totalWallLengthM(graph) {
  const byId = new Map(graph.vertices.map((v) => [v.id, v]))
  let sum = 0
  for (const w of graph.walls) {
    const a = byId.get(w.a)
    const b = byId.get(w.b)
    if (a && b) sum += Math.hypot(b.xM - a.xM, b.yM - a.yM)
  }
  return sum
}

// Add a rectangular room. Each corner welds onto an existing vertex within
// WELD_TOLERANCE_M; each edge is added only if no wall already connects that
// vertex pair — so the shared edge between two adjacent rooms collapses to a
// single wall instead of two coincident ones.
export function addRoomRect(graph, { xM, yM, widthM, heightM, thicknessM = DEFAULT_THICKNESS_M }) {
  const g = cloneGraph(graph)
  // A degenerate rectangle would weld its corners into 1–2 vertices and emit
  // zero-length / dropped walls — corrupting the graph silently. The drawing
  // tool guards with MIN_ROOM_M, but addRoomRect is public: reject here too.
  if (!(widthM > 0) || !(heightM > 0)) return g
  const corners = [
    [xM, yM],
    [xM + widthM, yM],
    [xM + widthM, yM + heightM],
    [xM, yM + heightM],
  ]
  const ids = corners.map(([cx, cy]) => findOrAddVertex(g, cx, cy))
  for (let i = 0; i < 4; i++) {
    addWallIfAbsent(g, ids[i], ids[(i + 1) % 4], thicknessM)
  }
  // A room drawn against an existing wall lands its corners on that wall's
  // interior. Split the wall at those points (T-junctions) so it divides into
  // segments instead of leaving a full-length wall with a duplicate stacked on
  // top (which shows up as overlapping dimension labels).
  normalizeGraph(g)
  return g
}

export function moveVertex(graph, vertexId, xM, yM) {
  const g = cloneGraph(graph)
  const v = g.vertices.find((x) => x.id === vertexId)
  if (v) {
    v.xM = xM
    v.yM = yM
  }
  return g
}

// Move a vertex and then settle the graph: weld it onto a coincident vertex if
// it landed on one (dropping the resulting zero-length walls and duplicates),
// and split any wall it now sits on (T-junction). Use this for interactive
// vertex drags so a user can't leave degenerate coincident vertices behind.
export function settleVertex(graph, vertexId, xM, yM) {
  const g = cloneGraph(graph)
  const v = g.vertices.find((x) => x.id === vertexId)
  if (!v) return g
  v.xM = xM
  v.yM = yM
  const target = g.vertices.find(
    (x) => x.id !== vertexId && Math.hypot(x.xM - xM, x.yM - yM) <= WELD_TOLERANCE_M,
  )
  if (target) {
    const kept = []
    for (const w of g.walls) {
      const a = w.a === vertexId ? target.id : w.a
      const b = w.b === vertexId ? target.id : w.b
      if (a === b) continue // collapsed to zero length — drop
      if (kept.some((k) => (k.a === a && k.b === b) || (k.a === b && k.b === a))) continue // dup
      kept.push({ ...w, a, b })
    }
    g.walls = kept
    g.vertices = g.vertices.filter((x) => x.id !== vertexId)
  }
  normalizeGraph(g)
  return g
}

// Resize a wall to `newLenM` metres by sliding ONE of its endpoints along the
// wall's own axis (the other stays put). `anchor` selects the fixed endpoint:
//   "a" — wall.a stays, wall.b slides → the wall grows/shrinks from b's side.
//   "b" — wall.b stays, wall.a slides.
// The slide goes through settleVertex, so any wall sharing the moved corner is
// dragged with it and the result stays planar (welds/T-splits handled). This is
// the planar-graph analogue of the plot editor's resizeEdgeAlongAxis — moving a
// shared corner can tilt a perpendicular neighbour (orthogonality is the job of
// the angle-snap drag, not this exact-length edit). Returns a NEW graph, or null
// for a bad length / unknown wall / zero-length wall.
export function resizeWallAlongAxis(graph, wallId, newLenM, anchor = "a") {
  if (!Number.isFinite(newLenM) || newLenM <= 0) return null
  const w = graph.walls.find((x) => x.id === wallId)
  if (!w) return null
  const byId = new Map(graph.vertices.map((v) => [v.id, v]))
  const a = byId.get(w.a)
  const b = byId.get(w.b)
  if (!a || !b) return null
  const curLen = Math.hypot(b.xM - a.xM, b.yM - a.yM)
  if (curLen === 0) return null
  const ux = (b.xM - a.xM) / curLen
  const uy = (b.yM - a.yM) / curLen
  if (anchor === "b") {
    return settleVertex(graph, w.a, b.xM - ux * newLenM, b.yM - uy * newLenM)
  }
  return settleVertex(graph, w.b, a.xM + ux * newLenM, a.yM + uy * newLenM)
}

// Remove a wall and any vertex left unreferenced by the remaining walls.
export function removeWall(graph, wallId) {
  const g = cloneGraph(graph)
  g.walls = g.walls.filter((w) => w.id !== wallId)
  const used = new Set()
  for (const w of g.walls) {
    used.add(w.a)
    used.add(w.b)
  }
  g.vertices = g.vertices.filter((v) => used.has(v.id))
  return g
}

function findOrAddVertex(g, xM, yM) {
  const existing = vertexAt(g, xM, yM)
  if (existing) return existing.id
  const id = `v${++g._seq}`
  g.vertices.push({ id, xM, yM })
  return id
}

function wallBetween(g, a, b) {
  return g.walls.find((w) => (w.a === a && w.b === b) || (w.a === b && w.b === a))
}

function addWallIfAbsent(g, a, b, thicknessM) {
  if (a === b) return
  if (wallBetween(g, a, b)) return
  g.walls.push({ id: `w${++g._seq}`, a, b, thicknessM, kind: "exterior" })
}

const ON_SEG_TOL_M = 0.01

function distPointSeg(px, py, ax, ay, bx, by) {
  const dx = bx - ax
  const dy = by - ay
  const len2 = dx * dx + dy * dy
  if (len2 === 0) return Math.hypot(px - ax, py - ay)
  let t = ((px - ax) * dx + (py - ay) * dy) / len2
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy))
}

// True when vertex v lies on wall (a..b)'s interior: on the segment within
// tolerance and strictly between the endpoints (not coincident with either).
function vertexOnWallInterior(v, a, b) {
  if (Math.hypot(v.xM - a.xM, v.yM - a.yM) <= ON_SEG_TOL_M) return false
  if (Math.hypot(v.xM - b.xM, v.yM - b.yM) <= ON_SEG_TOL_M) return false
  return distPointSeg(v.xM, v.yM, a.xM, a.yM, b.xM, b.yM) <= ON_SEG_TOL_M
}

// Intersection point of two segments [a,b] and [c,d] when they cross at a
// single point STRICTLY interior to both (not at a shared endpoint, not
// collinear). Returns [x, y] or null. This is what makes the graph planar:
// two walls drawn across each other (e.g. a "+" of partitions, or two
// overlapping room rectangles) meet at a point with no vertex, which would
// otherwise corrupt the face-detection half-edge traversal.
function segCrossingPoint(a, b, c, d) {
  const rx = b.xM - a.xM, ry = b.yM - a.yM
  const sx = d.xM - c.xM, sy = d.yM - c.yM
  const rxs = rx * sy - ry * sx
  if (Math.abs(rxs) < 1e-12) return null // parallel or collinear
  const qpx = c.xM - a.xM, qpy = c.yM - a.yM
  const t = (qpx * sy - qpy * sx) / rxs
  const u = (qpx * ry - qpy * rx) / rxs
  const eps = 1e-9
  if (t <= eps || t >= 1 - eps || u <= eps || u >= 1 - eps) return null
  return [a.xM + t * rx, a.yM + t * ry]
}

// Add a vertex at the first wall–wall crossing found (a pair sharing no
// endpoint that cross interior-to-interior). Returns true if one was inserted.
// The new vertex then sits on both walls' interiors, so the T-junction pass
// below splits them. Mutates g.
function splitFirstCrossing(g) {
  const byId = new Map(g.vertices.map((v) => [v.id, v]))
  for (let i = 0; i < g.walls.length; i++) {
    const w1 = g.walls[i]
    for (let j = i + 1; j < g.walls.length; j++) {
      const w2 = g.walls[j]
      if (w1.a === w2.a || w1.a === w2.b || w1.b === w2.a || w1.b === w2.b) continue
      const a = byId.get(w1.a), b = byId.get(w1.b)
      const c = byId.get(w2.a), d = byId.get(w2.b)
      if (!a || !b || !c || !d) continue
      const p = segCrossingPoint(a, b, c, d)
      if (!p) continue
      if (vertexAt(g, p[0], p[1])) continue // crossing already noded
      findOrAddVertex(g, p[0], p[1])
      return true
    }
  }
  return false
}

// Split the first wall that has a vertex on its interior into sub-walls at
// those vertices (T-junction / collinear-overlap split). Returns true if it
// split one. Mutates g.
function splitFirstVertexOnWall(g) {
  for (const w of g.walls) {
    const a = g.vertices.find((v) => v.id === w.a)
    const b = g.vertices.find((v) => v.id === w.b)
    if (!a || !b) continue
    const on = g.vertices.filter((v) => v.id !== w.a && v.id !== w.b && vertexOnWallInterior(v, a, b))
    if (on.length === 0) continue
    on.sort((p, q) => Math.hypot(p.xM - a.xM, p.yM - a.yM) - Math.hypot(q.xM - a.xM, q.yM - a.yM))
    g.walls = g.walls.filter((x) => x.id !== w.id)
    let prev = w.a
    for (const v of on) {
      addWallIfAbsent(g, prev, v.id, w.thicknessM)
      prev = v.id
    }
    addWallIfAbsent(g, prev, w.b, w.thicknessM)
    return true
  }
  return false
}

// Make the graph planar so face detection is well-defined: first node every
// wall–wall crossing, then split walls at any vertex on their interior (the
// crossing vertices and weld-aligned corners). Iterates to a fixed point;
// bounded by a guard against pathological non-convergence. Mutates g.
function normalizeGraph(g) {
  let guard = 0
  while (guard++ < 4000) {
    if (splitFirstCrossing(g)) continue
    if (splitFirstVertexOnWall(g)) continue
    break
  }
}
